/**
 * Component Demo Page
 *
 * Demonstrates all variants and states of the reusable component library.
 * This page is for development and testing purposes.
 */

import { useState } from 'react';
import { Button } from '../components/common/Button';
import { Input } from '../components/common/Input';
import { Card } from '../components/common/Card';
import { Modal } from '../components/common/Modal';
import './ComponentDemo.css';

export function ComponentDemo() {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [inputError, setInputError] = useState('');

  const handleInputChange = (value: string) => {
    setInputValue(value);
    if (value.length > 0 && value.length < 3) {
      setInputError('Must be at least 3 characters');
    } else {
      setInputError('');
    }
  };

  return (
    <div className="component-demo">
      <div className="demo-container">
        <header className="demo-header">
          <h1>Component Library Demo</h1>
          <p className="demo-subtitle">
            Reusable components for the Food Tracker application
          </p>
        </header>

        {/* Button Component Section */}
        <section className="demo-section">
          <h2 className="section-title">Button Component</h2>

          <div className="demo-subsection">
            <h3>Variants</h3>
            <div className="demo-grid">
              <Button variant="primary">Primary Button</Button>
              <Button variant="secondary">Secondary Button</Button>
              <Button variant="danger">Danger Button</Button>
              <Button variant="ghost">Ghost Button</Button>
            </div>
          </div>

          <div className="demo-subsection">
            <h3>Sizes</h3>
            <div className="demo-grid">
              <Button size="small">Small Button</Button>
              <Button size="medium">Medium Button</Button>
              <Button size="large">Large Button</Button>
            </div>
          </div>

          <div className="demo-subsection">
            <h3>States</h3>
            <div className="demo-grid">
              <Button disabled>Disabled Button</Button>
              <Button isLoading>Loading Button</Button>
              <Button leftIcon="🔍">With Left Icon</Button>
              <Button rightIcon="→">With Right Icon</Button>
            </div>
          </div>

          <div className="demo-subsection">
            <h3>Full Width</h3>
            <Button fullWidth>Full Width Button</Button>
          </div>
        </section>

        {/* Input Component Section */}
        <section className="demo-section">
          <h2 className="section-title">Input Component</h2>

          <div className="demo-subsection">
            <h3>Basic Inputs</h3>
            <div className="demo-grid-vertical">
              <Input
                label="Email"
                type="email"
                placeholder="your@email.com"
                hint="We'll never share your email"
              />
              <Input
                label="Password"
                type="password"
                placeholder="••••••••"
                required
              />
              <Input
                label="Search"
                type="text"
                placeholder="Search recipes..."
                leftIcon="🔍"
              />
            </div>
          </div>

          <div className="demo-subsection">
            <h3>With Validation</h3>
            <div className="demo-grid-vertical">
              <Input
                label="Username"
                type="text"
                value={inputValue}
                onChange={(e) => handleInputChange(e.target.value)}
                error={inputError}
                placeholder="Enter username"
              />
              <Input
                label="Disabled Input"
                type="text"
                placeholder="Cannot edit"
                disabled
              />
            </div>
          </div>
        </section>

        {/* Card Component Section */}
        <section className="demo-section">
          <h2 className="section-title">Card Component</h2>

          <div className="demo-subsection">
            <h3>Variants</h3>
            <div className="demo-grid">
              <Card variant="default">
                <h4>Default Card</h4>
                <p>This is a default card with standard styling.</p>
              </Card>
              <Card variant="outlined">
                <h4>Outlined Card</h4>
                <p>This is an outlined card with transparent background.</p>
              </Card>
              <Card variant="elevated">
                <h4>Elevated Card</h4>
                <p>This is an elevated card with shadow.</p>
              </Card>
            </div>
          </div>

          <div className="demo-subsection">
            <h3>With Header and Footer</h3>
            <Card
              header={<h4>Card Header</h4>}
              footer={
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <Button size="small" variant="ghost">
                    Cancel
                  </Button>
                  <Button size="small">Save</Button>
                </div>
              }
            >
              <p>
                This card has a header and footer. The footer contains action
                buttons.
              </p>
            </Card>
          </div>

          <div className="demo-subsection">
            <h3>Clickable Card</h3>
            <Card clickable onClick={() => alert('Card clicked!')}>
              <h4>Click Me!</h4>
              <p>This card is clickable and shows a hover effect.</p>
            </Card>
          </div>

          <div className="demo-subsection">
            <h3>Padding Variants</h3>
            <div className="demo-grid">
              <Card padding="small">
                <h4>Small Padding</h4>
              </Card>
              <Card padding="medium">
                <h4>Medium Padding</h4>
              </Card>
              <Card padding="large">
                <h4>Large Padding</h4>
              </Card>
            </div>
          </div>
        </section>

        {/* Modal Component Section */}
        <section className="demo-section">
          <h2 className="section-title">Modal Component</h2>

          <div className="demo-subsection">
            <h3>Open Modal</h3>
            <div className="demo-grid">
              <Button onClick={() => setIsModalOpen(true)}>
                Open Modal
              </Button>
            </div>

            <Modal
              isOpen={isModalOpen}
              onClose={() => setIsModalOpen(false)}
              title="Example Modal"
              size="medium"
              footer={
                <div style={{ display: 'flex', gap: '0.75rem' }}>
                  <Button
                    variant="ghost"
                    onClick={() => setIsModalOpen(false)}
                  >
                    Cancel
                  </Button>
                  <Button onClick={() => setIsModalOpen(false)}>
                    Confirm
                  </Button>
                </div>
              }
            >
              <p>
                This is a modal dialog. It includes a title, close button,
                body content, and footer with action buttons.
              </p>
              <p>
                You can close this modal by clicking the X button, clicking
                outside the modal, or pressing the ESC key.
              </p>
              <div style={{ marginTop: '1rem' }}>
                <Input
                  label="Example Input in Modal"
                  placeholder="Type something..."
                />
              </div>
            </Modal>
          </div>

          <div className="demo-info">
            <p>
              <strong>Modal Features:</strong>
            </p>
            <ul>
              <li>Focus trap (Tab navigation stays within modal)</li>
              <li>ESC key closes the modal</li>
              <li>Click overlay to close</li>
              <li>Accessible with ARIA attributes</li>
              <li>Multiple sizes (small, medium, large, full)</li>
            </ul>
          </div>
        </section>

        {/* Usage Examples Section */}
        <section className="demo-section">
          <h2 className="section-title">Usage Examples</h2>

          <Card>
            <h3>Login Form Example</h3>
            <div style={{ marginTop: '1rem', maxWidth: '400px' }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                <Input
                  label="Email"
                  type="email"
                  placeholder="your@email.com"
                  leftIcon="✉️"
                />
                <Input
                  label="Password"
                  type="password"
                  placeholder="••••••••"
                  leftIcon="🔒"
                  required
                />
                <Button fullWidth>Login</Button>
              </div>
            </div>
          </Card>
        </section>
      </div>
    </div>
  );
}

export default ComponentDemo;
